/**
 * Files attached in the agent-creation assistant (the embedded Elora Chat
 * widget, mode=agent_creation). Mirrors Elora-chat-frontend's
 * src/helpers/agentFileUploads.ts, which posts:
 *
 *   { type: 'elora:agent-files:attach',
 *     payload: { kind: 'knowledge' | 'employee_directory', files: File[] } }
 *
 * The widget checks the same limits before sending, but the files still come
 * from another origin, so callers validate them again before holding them
 * for Create.
 */
export const AGENT_FILES_ATTACH = 'elora:agent-files:attach';

export type AgentFileKind = 'knowledge' | 'employee_directory';

interface ListenForAgentFilesOptions {
  expectedOrigin: string;
  expectedSource: MessageEventSource | null;
  agentType: 'call' | 'chat';
  onFiles: (kind: AgentFileKind, files: File[]) => void;
}

const isAgentFileKind = (kind: unknown): kind is AgentFileKind =>
  kind === 'knowledge' || kind === 'employee_directory';

export const listenForAgentFiles = ({
  expectedOrigin,
  expectedSource,
  agentType,
  onFiles,
}: ListenForAgentFilesOptions): (() => void) => {
  const handler = (event: MessageEvent) => {
    if (event.origin !== expectedOrigin) return;
    if (!expectedSource || event.source !== expectedSource) return;
    if (event.data?.type !== AGENT_FILES_ATTACH) return;

    const { kind, files } = event.data.payload ?? {};
    if (!isAgentFileKind(kind)) return;
    if (!Array.isArray(files) || !files.every(file => file instanceof File)) {
      return;
    }
    // Chat agents have no Capabilities step to receive a directory.
    if (kind === 'employee_directory' && agentType !== 'call') return;

    onFiles(kind, files);
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
};
